import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Button } from '@mui/material';
import { Chat, ChevronRight, OpenInNew } from '@mui/icons-material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import {
  useGetProviderInfoQuery,
  useGetRecentStreamingThreadsQuery,
} from '../store/api';
import type { StreamingThreadListItem } from '../store/api';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';

// Workflow configurations - add new workflows here
const WORKFLOWS = [
  { name: 'note_chat', label: 'Note Chat', limit: 20 },
  // Add more workflows as needed:
  // { name: 'another_workflow', label: 'Another Workflow', limit: 20 },
] as const;

// Max threads we'll fetch for the provider-scoped view (backend caps at 100).
const PROVIDER_SCOPED_LIMIT = 100;

export default function StreamingThreadsList() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;
  const isProviderScoped = !!providerId;

  const [selectedWorkflow, setSelectedWorkflow] = useState(0);
  const currentWorkflow = WORKFLOWS[selectedWorkflow];

  const { data: provider, isLoading: providerLoading } = useGetProviderInfoQuery(
    { providerId: providerId || '' },
    { skip: !providerId },
  );

  const { data, isLoading, error, refetch } = useGetRecentStreamingThreadsQuery({
    limit: isProviderScoped ? PROVIDER_SCOPED_LIMIT : currentWorkflow.limit,
    workflowName: currentWorkflow.name,
    userId: providerId || undefined,
  });

  const columns = [
    {
      key: 'thread_id' as keyof StreamingThreadListItem,
      label: 'Thread ID',
      render: (value: unknown) => (
        <Typography variant="body2" sx={{fontFamily: "monospace",  fontSize: "0.85rem" }}>
          {String(value).substring(0, 12)}...
        </Typography>
      ),
    },
    {
      key: 'user_name' as keyof StreamingThreadListItem,
      label: 'User',
      render: (value: unknown, row: StreamingThreadListItem) => (
        <Typography variant="body2">
          {value ? String(value) : row.user_id?.substring(0, 8) + '...'}
        </Typography>
      ),
    },
    {
      key: 'initial_message' as keyof StreamingThreadListItem,
      label: 'Initial Message',
      render: (value: unknown) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 300,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {String(value)}
        </Typography>
      ),
    },
    {
      key: 'template_name' as keyof StreamingThreadListItem,
      label: 'Template',
      render: (value: unknown, row: StreamingThreadListItem) => {
        if (value) {
          return (
            <Typography
              variant="body2"
              sx={{
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={String(value)}
            >
              {String(value)}
            </Typography>
          );
        }
        if (row.template_id) {
          return (
            <Typography variant="body2" sx={{fontFamily: "monospace",  fontSize: "0.85rem" }}>
              {row.template_id.substring(0, 8)}...
            </Typography>
          );
        }
        return (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        );
      },
    },
    {
      key: 'event_count' as keyof StreamingThreadListItem,
      label: 'Events',
      render: (value: unknown) => (
        <Typography variant="body2">{String(value)}</Typography>
      ),
    },
    {
      key: 'first_message_at' as keyof StreamingThreadListItem,
      label: 'Started',
      render: (value: unknown) => (
        <Box>
          <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(value as string)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'last_message_at' as keyof StreamingThreadListItem,
      label: 'Last Activity',
      render: (value: unknown) => (
        <Typography variant="body2">{formatRelativeTime(value as string)}</Typography>
      ),
    },
  ];

  const getRowUrl = (row: StreamingThreadListItem) => {
    const base = `/admin-v2/streaming-thread/${row.thread_id}`;
    return isProviderScoped
      ? `${base}?userId=${encodeURIComponent(providerId || '')}`
      : base;
  };

  const tableTitle = isProviderScoped
    ? `${currentWorkflow.label} - All Threads (${data?.threads?.length || 0})`
    : `${currentWorkflow.label} - Recent Threads (${data?.threads?.length || 0})`;

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton
            fallbackUrl={
              isProviderScoped
                ? `/admin-v2/provider/${encodeURIComponent(providerId || '')}`
                : '/admin-v2'
            }
          />
        </div>

        {isProviderScoped && (
          <nav className="flex mb-6" aria-label="Breadcrumb">
            <ol className="inline-flex items-center space-x-1 md:space-x-3">
              <li className="inline-flex items-center">
                <Link to="/admin-v2">
                  <Button
                    variant="text"
                    className="text-sm font-medium text-gray-700 hover:text-blue-600"
                  >
                    <OpenInNew className="mr-2 h-4 w-4" />
                    Search
                  </Button>
                </Link>
              </li>
              {provider && (
                <li>
                  <div className="flex items-center">
                    <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                    <Link to={`/admin-v2/provider/${encodeURIComponent(providerId || '')}`}>
                      <Button
                        variant="text"
                        className="text-sm font-medium text-gray-700 hover:text-blue-600"
                      >
                        {provider.name}
                      </Button>
                    </Link>
                  </div>
                </li>
              )}
              <li>
                <div className="flex items-center">
                  <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                  <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                    Streaming Threads
                  </span>
                </div>
              </li>
            </ol>
          </nav>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Chat fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                Streaming Threads
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isProviderScoped
                  ? provider
                    ? `All AI agent conversation threads for ${provider.name}`
                    : providerLoading
                      ? 'Loading provider...'
                      : 'All AI agent conversation threads for this provider'
                  : 'Recent AI agent conversation threads'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Workflow Tabs - only show if multiple workflows exist */}
        {WORKFLOWS.length > 1 && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={selectedWorkflow}
              onChange={(_, newValue: number) => setSelectedWorkflow(newValue)}
              aria-label="workflow tabs"
            >
              {WORKFLOWS.map((workflow, index) => (
                <Tab key={workflow.name} label={workflow.label} id={`workflow-tab-${index}`} />
              ))}
            </Tabs>
          </Box>
        )}

        {error ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography color="error" variant="h6" sx={{ mb: 2 }}>
              Failed to load threads
            </Typography>
            <button
              onClick={() => void refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Retry
            </button>
          </Box>
        ) : (
          <DataTable<StreamingThreadListItem>
            title={tableTitle}
            columns={columns}
            data={data?.threads || []}
            loading={isLoading}
            getRowUrl={getRowUrl}
            emptyMessage="No streaming threads found"
          />
        )}
      </main>
    </div>
  );
}
